/**
 * Determines if two numbers roughly equal each other (if they're difference is less than the
 * epsilon)
 * @param {Number} num1
 * @param {Number} num2
 * @param {Number} [epsilon=0.0001]: Max difference allowed before returning false
 * @return {Boolean}
 */
function closelyEqual(num1, num2, epsilon = 0.0001) {
  return Math.abs(num1 - num2) < epsilon;
}

/**
 * Does an equality check on the center arrays and zoom values of each viewport
 * @param {Object} v1: viewport object containing center and zoom keys
 * @param {Object} v2: viewport object containing center and zoom keys
 * @return {Boolean}
 */
export function equalViewports(v1, v2) {
  return (
    v1.zoom === v2.zoom &&
    closelyEqual(v1.center[0], v2.center[0]) &&
    closelyEqual(v1.center[1], v2.center[1])
  );
}

const maxMercatorLatitude = 85.05112878;
const controlledViewportPixelTolerance = 0.25;

function projectViewportCenter([latitude, longitude], zoom) {
  const scale = 256 * (2 ** zoom);
  const clampedLatitude = Math.max(
    -maxMercatorLatitude,
    Math.min(maxMercatorLatitude, latitude)
  );
  const latitudeRadians = clampedLatitude * Math.PI / 180;

  return {
    scale,
    x: ((longitude + 180) / 360) * scale,
    y: (
      0.5 -
      Math.log(
        (1 + Math.sin(latitudeRadians)) /
        (1 - Math.sin(latitudeRadians))
      ) / (4 * Math.PI)
    ) * scale,
  };
}

export function equalControlledViewports(v1, v2) {
  if (
    v1.zoom !== v2.zoom ||
    !v1.center ||
    !v2.center
  ) {
    return false;
  }

  const first = projectViewportCenter(v1.center, v1.zoom);
  const second = projectViewportCenter(v2.center, v2.zoom);
  const horizontalDifference = Math.abs(
    ((((first.x - second.x) + first.scale / 2) % first.scale) +
      first.scale) %
      first.scale -
      first.scale / 2
  );
  const verticalDifference = Math.abs(first.y - second.y);

  return (
    horizontalDifference < controlledViewportPixelTolerance &&
    verticalDifference < controlledViewportPixelTolerance
  );
}
